// Grammar Units 12 Data - Unit 12: Prepositions Complete Learning Course
// Based on Ch12 (1st Half).jsx and Ch12 (2nd Half).jsx
// Organized for Supabase migration and systematic data management

export const grammarUnits12Data = [
  {
    id: 12,
    title: { traditional: '介系詞完全學習課程', simplified: '介系词完全学习课程', english: 'Prepositions Complete Learning Course' },
    subtitle: { traditional: '掌握各種介系詞的用法', simplified: '掌握各种介系词的用法', english: 'Master various uses of prepositions' },
    description: { traditional: '系統性學習英文介系詞，包括時間、地點、方向、方式、原因等各種用法。', simplified: '系统性学习英文介系词，包括时间、地点、方向、方式、原因等各种用法。', english: 'Systematic learning of English prepositions including time, place, direction, manner, cause and other various uses.' },
    icon: '📍',
    color: 'from-teal-500 to-cyan-600',
    status: 'notStarted',
    difficulty: 'intermediate',
    estimatedHours: 4,
    content: {
      overview: {
        title: "介系詞概覽",
        description: "介系詞是用來表示名詞或代名詞與其他詞類之間關係的詞類，通常用來表示時間、地點、方向、方式、原因等。",
        types: [
          { type: "時間介系詞", examples: "at, in, on, before, after, during", function: "表示時間關係" },
          { type: "地點介系詞", examples: "at, in, on, under, over, between", function: "表示位置關係" },
          { type: "方向介系詞", examples: "to, from, into, out of, through", function: "表示方向關係" },
          { type: "方式介系詞", examples: "by, with, without, like", function: "表示方式關係" },
          { type: "原因介系詞", examples: "because of, due to, thanks to", function: "表示原因關係" }
        ]
      },
      time_prepositions: {
        title: "時間介系詞 (Time Prepositions)",
        description: "時間介系詞用來表示時間關係，包括具體時間點、時間段、時間順序等。",
        prepositions: [
          {
            word: "at",
            meaning: "在...時",
            usage: "表示具體的時間點",
            examples: [
              "at 3 o'clock",
              "at noon",
              "at midnight",
              "at sunrise",
              "at sunset",
              "at the moment",
              "at present",
              "at that time"
            ]
          },
          {
            word: "in",
            meaning: "在...內",
            usage: "表示較長的時間段",
            examples: [
              "in the morning",
              "in the afternoon",
              "in the evening",
              "in January",
              "in 2023",
              "in the summer",
              "in the past",
              "in the future"
            ]
          },
          {
            word: "on",
            meaning: "在...上",
            usage: "表示特定的日期或星期",
            examples: [
              "on Monday",
              "on January 1st",
              "on my birthday",
              "on Christmas Day",
              "on time",
              "on schedule"
            ]
          },
          {
            word: "before",
            meaning: "在...之前",
            usage: "表示時間順序",
            examples: [
              "before dinner",
              "before the meeting",
              "before I left",
              "before sunrise"
            ]
          },
          {
            word: "after",
            meaning: "在...之後",
            usage: "表示時間順序",
            examples: [
              "after lunch",
              "after the movie",
              "after I finished",
              "after sunset"
            ]
          },
          {
            word: "during",
            meaning: "在...期間",
            usage: "表示在某段時間內",
            examples: [
              "during the meeting",
              "during the summer",
              "during my vacation",
              "during the night"
            ]
          },
          {
            word: "since",
            meaning: "自從...以來",
            usage: "表示從過去某個時間點開始",
            examples: [
              "since Monday",
              "since January",
              "since I was a child",
              "since last year"
            ]
          },
          {
            word: "for",
            meaning: "持續...時間",
            usage: "表示時間長度",
            examples: [
              "for two hours",
              "for a week",
              "for years",
              "for a long time"
            ]
          },
          {
            word: "until",
            meaning: "直到...為止",
            usage: "表示時間終點",
            examples: [
              "until 5 o'clock",
              "until tomorrow",
              "until I finish",
              "until the end"
            ]
          },
          {
            word: "by",
            meaning: "到...為止",
            usage: "表示截止時間",
            examples: [
              "by Friday",
              "by the end of the month",
              "by next week",
              "by then"
            ]
          }
        ]
      },
      place_prepositions: {
        title: "地點介系詞 (Place Prepositions)",
        description: "地點介系詞用來表示位置關係，包括具體位置、相對位置等。",
        prepositions: [
          {
            word: "at",
            meaning: "在...",
            usage: "表示具體的地點或位置",
            examples: [
              "at home",
              "at school",
              "at the office",
              "at the station",
              "at the corner",
              "at the top",
              "at the bottom"
            ]
          },
          {
            word: "in",
            meaning: "在...內",
            usage: "表示在封閉空間內",
            examples: [
              "in the room",
              "in the car",
              "in the building",
              "in the city",
              "in the country",
              "in the world"
            ]
          },
          {
            word: "on",
            meaning: "在...上",
            usage: "表示在表面或平面上",
            examples: [
              "on the table",
              "on the wall",
              "on the floor",
              "on the street",
              "on the bus",
              "on the train"
            ]
          },
          {
            word: "under",
            meaning: "在...下",
            usage: "表示在下方",
            examples: [
              "under the table",
              "under the bridge",
              "under the tree",
              "under the water"
            ]
          },
          {
            word: "over",
            meaning: "在...上方",
            usage: "表示在上方",
            examples: [
              "over the bridge",
              "over the mountain",
              "over the city",
              "over the table"
            ]
          },
          {
            word: "between",
            meaning: "在...之間",
            usage: "表示在兩個事物之間",
            examples: [
              "between two trees",
              "between you and me",
              "between the houses",
              "between 2 and 4 o'clock"
            ]
          },
          {
            word: "among",
            meaning: "在...之中",
            usage: "表示在多個事物之中",
            examples: [
              "among the students",
              "among the trees",
              "among the crowd",
              "among friends"
            ]
          },
          {
            word: "next to",
            meaning: "在...旁邊",
            usage: "表示相鄰位置",
            examples: [
              "next to the door",
              "next to me",
              "next to the bank",
              "next to the park"
            ]
          },
          {
            word: "beside",
            meaning: "在...旁邊",
            usage: "表示相鄰位置",
            examples: [
              "beside the river",
              "beside me",
              "beside the house",
              "beside the road"
            ]
          },
          {
            word: "behind",
            meaning: "在...後面",
            usage: "表示在後方",
            examples: [
              "behind the house",
              "behind me",
              "behind the door",
              "behind the car"
            ]
          },
          {
            word: "in front of",
            meaning: "在...前面",
            usage: "表示在前方",
            examples: [
              "in front of the house",
              "in front of me",
              "in front of the door",
              "in front of the car"
            ]
          }
        ]
      },
      direction_prepositions: {
        title: "方向介系詞 (Direction Prepositions)",
        description: "方向介系詞用來表示運動方向或位置變化。",
        prepositions: [
          {
            word: "to",
            meaning: "到...",
            usage: "表示目的地",
            examples: [
              "go to school",
              "walk to the store",
              "drive to work",
              "fly to Paris"
            ]
          },
          {
            word: "from",
            meaning: "從...",
            usage: "表示起點",
            examples: [
              "come from home",
              "walk from the station",
              "drive from work",
              "fly from London"
            ]
          },
          {
            word: "into",
            meaning: "進入...",
            usage: "表示進入某個空間",
            examples: [
              "walk into the room",
              "drive into the garage",
              "jump into the water",
              "go into the building"
            ]
          },
          {
            word: "out of",
            meaning: "從...出來",
            usage: "表示離開某個空間",
            examples: [
              "walk out of the room",
              "drive out of the garage",
              "get out of the car",
              "come out of the house"
            ]
          },
          {
            word: "through",
            meaning: "穿過...",
            usage: "表示穿過某個空間",
            examples: [
              "walk through the park",
              "drive through the tunnel",
              "go through the door",
              "swim through the water"
            ]
          },
          {
            word: "across",
            meaning: "橫過...",
            usage: "表示橫向穿過",
            examples: [
              "walk across the street",
              "swim across the river",
              "drive across the bridge",
              "run across the field"
            ]
          },
          {
            word: "up",
            meaning: "向上",
            usage: "表示向上運動",
            examples: [
              "climb up the mountain",
              "walk up the stairs",
              "go up the hill",
              "look up at the sky"
            ]
          },
          {
            word: "down",
            meaning: "向下",
            usage: "表示向下運動",
            examples: [
              "walk down the stairs",
              "go down the hill",
              "climb down the ladder",
              "look down at the ground"
            ]
          }
        ]
      },
      manner_prepositions: {
        title: "方式介系詞 (Manner Prepositions)",
        description: "方式介系詞用來表示方式、方法或工具。",
        prepositions: [
          {
            word: "by",
            meaning: "通過...",
            usage: "表示方式或方法",
            examples: [
              "by car",
              "by train",
              "by plane",
              "by hand",
              "by email",
              "by phone"
            ]
          },
          {
            word: "with",
            meaning: "用...",
            usage: "表示工具或伴隨",
            examples: [
              "write with a pen",
              "cut with a knife",
              "eat with chopsticks",
              "go with friends"
            ]
          },
          {
            word: "without",
            meaning: "沒有...",
            usage: "表示缺乏或沒有",
            examples: [
              "without money",
              "without help",
              "without permission",
              "without thinking"
            ]
          },
          {
            word: "like",
            meaning: "像...",
            usage: "表示相似或比較",
            examples: [
              "like a bird",
              "like a child",
              "like a professional",
              "like a dream"
            ]
          }
        ]
      },
      cause_prepositions: {
        title: "原因介系詞 (Cause Prepositions)",
        description: "原因介系詞用來表示原因或理由。",
        prepositions: [
          {
            word: "because of",
            meaning: "因為...",
            usage: "表示原因",
            examples: [
              "because of the rain",
              "because of you",
              "because of the weather",
              "because of the delay"
            ]
          },
          {
            word: "due to",
            meaning: "由於...",
            usage: "表示原因",
            examples: [
              "due to bad weather",
              "due to illness",
              "due to traffic",
              "due to circumstances"
            ]
          },
          {
            word: "thanks to",
            meaning: "多虧...",
            usage: "表示感謝或原因",
            examples: [
              "thanks to your help",
              "thanks to the weather",
              "thanks to technology",
              "thanks to everyone"
            ]
          },
          {
            word: "owing to",
            meaning: "由於...",
            usage: "表示原因",
            examples: [
              "owing to bad weather",
              "owing to circumstances",
              "owing to illness",
              "owing to the delay"
            ]
          }
        ]
      },
      exercises: {
        title: "練習題",
        sections: [
          {
            name: "介系詞練習 1",
            questions: [
              {
                question: "1. I'll meet you __________ 3 o'clock.",
                options: {
                  A: "at",
                  B: "in", 
                  C: "on",
                  D: "by"
                },
                answer: "A",
                explanation: "at 用來表示具體的時間點"
              },
              {
                question: "2. The book is __________ the table.",
                options: {
                  A: "at",
                  B: "in",
                  C: "on",
                  D: "by"
                },
                answer: "C",
                explanation: "on 用來表示在表面上"
              }
            ]
          },
          {
            name: "介系詞練習 2",
            questions: [
              {
                question: "1. I'll finish this __________ Friday.",
                options: {
                  A: "at",
                  B: "in",
                  C: "on",
                  D: "by"
                },
                answer: "D",
                explanation: "by 表示截止時間"
              },
              {
                question: "2. She walked __________ the park.",
                options: {
                  A: "to",
                  B: "from",
                  C: "through",
                  D: "across"
                },
                answer: "C",
                explanation: "through 表示穿過某個空間"
              }
            ]
          }
        ]
      }
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits12Data.find(unit => unit.id === unitId);
};

// Export units by difficulty
export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits12Data.filter(unit => unit.difficulty === difficulty);
};

// Export completed units
export const getCompletedUnits = () => {
  return grammarUnits12Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits12Data;
